/**
 * Who stands where when a cast fires: the field a footprint is priced against.
 *
 * A cast aims at the nearest enemy `T`, at distance `D`. Three facts about the
 * live encounter decide what its footprint reaches, and a homogeneous Poisson
 * field with the nearest-enemy law states none of them:
 *
 * 1. WHEN. An automatic slot fires the moment an enemy is in reach, so a share of
 *    casts fires with `T` at the edge of the range a hunter engages from; the
 *    rest fire with `T` anywhere in reach, evenly (`castDistances`).
 * 2. EMPTY DISC. `T` is the nearest, so nobody stands closer to the caster than
 *    `D`: the disc of radius `D` around the caster holds no other enemy. A
 *    footprint pays no hits for the part of it inside that disc (`othersMass`).
 * 3. WHERE THE REST ARE. The other enemies are a camp, not a Poisson sprinkle:
 *    they stand on the far side of `T`, in a band along the line from the
 *    caster through `T`, thinning with the lateral distance from that line.
 *
 * Coordinates: the caster is the origin and the aim is `+y`, so `T = (0, D)`.
 * A footprint anchored on the target (an area that lands on `T`) is written
 * around `T` instead (`frame: 'TARGET'`), and its cells move with `D`.
 *
 * Measured, not fitted to a price: `CastFieldEvidence` in `context.ts`.
 */
import type { CastFieldEvidence } from '../context.js';
import type { Delivery } from '../types.js';
import type { SkillApplicationV2 } from '../skill-engine/types.js';
import type { ExpandedTransform } from '../skill-engine/composite-types.js';
import { IDENTITY_CAST_POSE, resolveAttackPose } from '../sim/attack-pose.js';
import { resolveWeaponContactEvaluation } from '../sim/weapon-contact.js';

/** A footprint reduced to what containment needs: a shape, placed and aimed. */
export type Footprint =
  | { readonly kind: 'DISC'; readonly x: number; readonly y: number; readonly radiusM: number }
  | {
      readonly kind: 'SECTOR';
      readonly x: number;
      readonly y: number;
      readonly reachM: number;
      readonly halfAngleRad: number;
      readonly facingRad: number;
    }
  | {
      readonly kind: 'CORRIDOR';
      readonly x: number;
      readonly y: number;
      readonly facingRad: number;
      readonly lengthM: number;
      readonly halfWidthM: number;
    };

export function footprintContains(shape: Footprint, px: number, py: number): boolean {
  const dx = px - shape.x;
  const dy = py - shape.y;
  if (shape.kind === 'DISC') return dx * dx + dy * dy <= shape.radiusM * shape.radiusM;
  if (shape.kind === 'SECTOR') {
    if (dx * dx + dy * dy > shape.reachM * shape.reachM) return false;
    // Signed angular distance to the facing, wrapped to (-pi, pi].
    let delta = Math.atan2(dy, dx) - shape.facingRad;
    delta = Math.atan2(Math.sin(delta), Math.cos(delta));
    return Math.abs(delta) <= shape.halfAngleRad;
  }
  const along = dx * Math.cos(shape.facingRad) + dy * Math.sin(shape.facingRad);
  const lateral = Math.abs(dx * Math.sin(shape.facingRad) - dy * Math.cos(shape.facingRad));
  return along >= 0 && along <= shape.lengthM && lateral <= shape.halfWidthM;
}

/** A footprint turned about the caster by `angleRad`. */
export function rotateFootprint(shape: Footprint, angleRad: number): Footprint {
  const cos = Math.cos(angleRad);
  const sin = Math.sin(angleRad);
  const turned = { x: shape.x * cos - shape.y * sin, y: shape.x * sin + shape.y * cos };
  return shape.kind === 'DISC' ? { ...shape, ...turned } : { ...shape, ...turned, facingRad: shape.facingRad + angleRad };
}

/** Step of the walk in along the aim (m). */
const ENTRY_STEP_M = 0.02;

/**
 * Where an enemy walking in along the aim (`+y`) first stands inside one of
 * the footprints: the outermost point of their union on that line, 0 when the
 * line meets none.
 */
export function entryDistanceM(shapes: readonly Footprint[]): number {
  const reach = Math.max(0, ...shapes.map(footprintReachM));
  for (let y = reach; y > 0; y -= ENTRY_STEP_M) {
    if (shapes.some((shape) => footprintContains(shape, 0, y))) return y;
  }
  return 0;
}

/** How far from the cast origin the footprint can contain a point. */
export function footprintReachM(shape: Footprint): number {
  const offset = Math.hypot(shape.x, shape.y);
  if (shape.kind === 'DISC') return offset + shape.radiusM;
  if (shape.kind === 'SECTOR') return offset + shape.reachM;
  return offset + shape.lengthM;
}

/**
 * How far from the caster the footprint reaches, before the enemy hitbox widens
 * it: the distance an automatic slot starts casting at. A corridor or a cone is
 * reached along its length from where it is placed ahead (`sim/auto-cast-reach.ts`
 * `compositeReachM`); a circle by its farthest edge, which for a ring turned with
 * the aim is the edge of the circle in front, and for a scatter laid down in the
 * world is the farthest any circle goes.
 */
export function footprintCastReachM(shape: Footprint, enemyRadiusM: number): number {
  if (shape.kind === 'DISC') return Math.hypot(shape.x, shape.y) + shape.radiusM - enemyRadiusM;
  return shape.y + (shape.kind === 'SECTOR' ? shape.reachM - enemyRadiusM : shape.lengthM);
}

/**
 * The region one member sweeps, dilated by the enemy hitbox exactly as
 * `targeting.ts` dilates it. `undefined` for a delivery that is not an area,
 * a cone or a corridor (a locked target or the caster itself), which has no
 * footprint a placed member could miss with.
 */
export function memberFootprint(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  transform: ExpandedTransform,
  enemyRadiusM: number,
): Footprint | undefined {
  const pose = resolveAttackPose(IDENTITY_CAST_POSE, transform);
  switch (delivery.type) {
    case 'ground_aoe':
      return { kind: 'DISC', x: pose.origin.x, y: pose.origin.y, radiusM: delivery.radiusM + enemyRadiusM };
    case 'projectile':
      return {
        kind: 'CORRIDOR', x: pose.origin.x, y: pose.origin.y, facingRad: pose.directionRad,
        lengthM: delivery.rangeM, halfWidthM: delivery.radiusM + enemyRadiusM,
      };
    case 'cone': {
      // A spear thrust is a corridor, not a wedge (`targeting.ts` prices it so).
      const thrust = application === undefined ? undefined : resolveWeaponContactEvaluation(application, enemyRadiusM)?.thrust;
      if (thrust !== undefined) {
        return {
          kind: 'CORRIDOR', x: pose.origin.x, y: pose.origin.y, facingRad: pose.directionRad,
          lengthM: thrust.reachM, halfWidthM: thrust.halfWidthM,
        };
      }
      return {
        kind: 'SECTOR', x: pose.origin.x, y: pose.origin.y, reachM: delivery.rangeM + enemyRadiusM,
        halfAngleRad: ((delivery.angleDeg / 2) * Math.PI) / 180, facingRad: pose.directionRad,
      };
    }
    default:
      return undefined;
  }
}

/**
 * Whether a delivery's hits are priced against the cast field: every footprint
 * a skill owns (projectile corridor, circle, cone). A weapon contact (a swing
 * or a thrust) is struck at arm's length, in a crowd the field does not
 * describe, and keeps the homogeneous model.
 */
export function usesCastField(delivery: Delivery, application: SkillApplicationV2 | undefined): boolean {
  return (delivery.type === 'projectile' || delivery.type === 'ground_aoe' || delivery.type === 'cone') && application?.geometry.owner !== 'WEAPON_CONTACT';
}

const IDENTITY_TRANSFORM: ExpandedTransform = Object.freeze({ x: 0, y: 0, directionRad: 0 });

/**
 * The region an unplaced delivery strikes and the reach it fires from: a
 * projectile's corridor and a caster circle from the caster, any other area
 * around the aimed enemy, which it lands on.
 */
export function unplacedCastRegion(
  delivery: Delivery,
  application: SkillApplicationV2 | undefined,
  enemyRadiusM: number,
  field: CastFieldEvidence,
): { readonly region: FieldRegion; readonly reachM: number } | undefined {
  if (!usesCastField(delivery, application)) return undefined;
  const footprint = memberFootprint(delivery, application, IDENTITY_TRANSFORM, enemyRadiusM);
  if (footprint === undefined) return undefined;
  const onTarget = delivery.type === 'ground_aoe' && delivery.center !== 'CASTER';
  return {
    region: footprintRegion(footprint, onTarget ? 'TARGET' : 'CASTER'),
    reachM: onTarget ? field.engageRangeM : footprintCastReachM(footprint, enemyRadiusM),
  };
}

/** A region of ground a cast strikes, as the field needs it. */
export interface FieldRegion {
  /** Whether an enemy standing at `(x, y)` is struck. */
  readonly contains: (x: number, y: number) => boolean;
  readonly bounds: { readonly minX: number; readonly maxX: number; readonly minY: number; readonly maxY: number };
  /** Where `(0, 0)` is: the caster, or the aimed enemy. */
  readonly frame: 'CASTER' | 'TARGET';
  /** Identifies the region for the profile cache. */
  readonly key: string;
}

/** The smallest box around a footprint. */
function footprintBounds(shape: Footprint): FieldRegion['bounds'] {
  if (shape.kind === 'DISC') return { minX: shape.x - shape.radiusM, maxX: shape.x + shape.radiusM, minY: shape.y - shape.radiusM, maxY: shape.y + shape.radiusM };
  if (shape.kind === 'SECTOR') return { minX: shape.x - shape.reachM, maxX: shape.x + shape.reachM, minY: shape.y - shape.reachM, maxY: shape.y + shape.reachM };
  const endX = shape.x + shape.lengthM * Math.cos(shape.facingRad);
  const endY = shape.y + shape.lengthM * Math.sin(shape.facingRad);
  return {
    minX: Math.min(shape.x, endX) - shape.halfWidthM, maxX: Math.max(shape.x, endX) + shape.halfWidthM,
    minY: Math.min(shape.y, endY) - shape.halfWidthM, maxY: Math.max(shape.y, endY) + shape.halfWidthM,
  };
}

export function footprintRegion(shape: Footprint, frame: FieldRegion['frame'], shift: { readonly x: number; readonly y: number } = { x: 0, y: 0 }): FieldRegion {
  const box = footprintBounds(shape);
  return {
    contains: (x, y) => footprintContains(shape, x + shift.x, y + shift.y),
    bounds: { minX: box.minX - shift.x, maxX: box.maxX - shift.x, minY: box.minY - shift.y, maxY: box.maxY - shift.y },
    frame,
    key: `${frame}|${JSON.stringify(shape)}|${shift.x},${shift.y}`,
  };
}

/** Several footprints struck as one (a shared union): an enemy in any of them is struck. */
export function unionRegion(shapes: readonly Footprint[], frame: FieldRegion['frame'], shift: { readonly x: number; readonly y: number } = { x: 0, y: 0 }): FieldRegion {
  const parts = shapes.map((shape) => footprintRegion(shape, frame, shift));
  return {
    contains: (x, y) => parts.some((part) => part.contains(x, y)),
    bounds: {
      minX: Math.min(...parts.map((part) => part.bounds.minX)), maxX: Math.max(...parts.map((part) => part.bounds.maxX)),
      minY: Math.min(...parts.map((part) => part.bounds.minY)), maxY: Math.max(...parts.map((part) => part.bounds.maxY)),
    },
    frame,
    key: parts.map((part) => part.key).join('+'),
  };
}

/** Distances the aimed enemy is drawn at, with their weights (summing to 1). */
export interface CastDistance {
  readonly distanceM: number;
  readonly weight: number;
}

/** Midpoint nodes of the even part of the distance law; fixed so the same plan yields the same bytes. */
const EVEN_NODES = 64;

/**
 * `T`'s distance when the cast fires: a share at the edge of the skill's own
 * reach (the cast fires as `T` comes into range), a share at the distance a
 * hunter engages from (it was not ready then, and fires when the hunter
 * stops), the rest evenly over the reach. A skill that reaches no further than
 * a hunter engages from has both shares at its reach. With `entry` the cast
 * fires only as an enemy steps into a fixed footprint, so every cast fires at
 * its reach.
 */
export function castDistances(reachM: number, field: CastFieldEvidence, entry = false): readonly CastDistance[] {
  const reach = Math.max(reachM, 0);
  // A cast that fires only when an enemy stands inside a fixed footprint fires as the first one steps in.
  if (entry) return [{ distanceM: Math.max(0, reach - field.firstOpportunityMarginM), weight: 1 }];
  const evenShare = 1 - field.reachEdgeShare - field.engageShare;
  const edge = (distanceM: number): number => Math.max(0, distanceM - field.firstOpportunityMarginM);
  const nodes: CastDistance[] = [
    { distanceM: edge(reach), weight: field.reachEdgeShare },
    { distanceM: edge(Math.min(reach, field.engageRangeM)), weight: field.engageShare },
  ];
  for (let i = 0; i < EVEN_NODES; i += 1) {
    nodes.push({ distanceM: ((i + 0.5) / EVEN_NODES) * reach, weight: evenShare / EVEN_NODES });
  }
  return nodes;
}

const CELL_M = 0.1;
const MAX_CELLS = 20000;

interface Cells { readonly xs: Float64Array; readonly ys: Float64Array; readonly cellAreaM2: number }

function sampleCells(region: FieldRegion, maxCells = MAX_CELLS): Cells {
  const { minX, maxX, minY, maxY } = region.bounds;
  const width = Math.max(0, maxX - minX);
  const height = Math.max(0, maxY - minY);
  const step = Math.max(CELL_M, Math.sqrt((width * height) / maxCells));
  // Whole cells that tile the box exactly: a thin corridor is not shaved by a partial last cell.
  const nx = Math.max(1, Math.ceil(width / step));
  const ny = Math.max(1, Math.ceil(height / step));
  const dx = width / nx;
  const dy = height / ny;
  const xs: number[] = [];
  const ys: number[] = [];
  for (let i = 0; i < nx; i += 1) {
    for (let j = 0; j < ny; j += 1) {
      const x = minX + (i + 0.5) * dx;
      const y = minY + (j + 0.5) * dy;
      if (region.contains(x, y)) { xs.push(x); ys.push(y); }
    }
  }
  return { xs: Float64Array.from(xs), ys: Float64Array.from(ys), cellAreaM2: dx * dy };
}

/**
 * The density of the other enemies at a point of a region when `T` stands at
 * distance `D`, per m² of the field's own density: 0 inside the empty disc,
 * thinning with the lateral distance from the line through `T`, and in front of it.
 */
export function othersDensityShape(x: number, cellY: number, frame: FieldRegion['frame'], distanceM: number, field: CastFieldEvidence): number {
  const y = frame === 'TARGET' ? cellY + distanceM : cellY;
  const r2 = x * x + y * y;
  if (r2 < distanceM * distanceM) return 0;
  const beyond = y - distanceM;
  return Math.exp(-Math.abs(x) / field.bandHalfWidthM) * (beyond >= 0 ? 1 : Math.exp(beyond / field.frontLengthM));
}

/** The density of `othersDensityShape` in enemies per m² (`bandDensityOverField` and the field's density applied). */
export const othersDensityScale = (field: CastFieldEvidence, enemyDensityPerM2: number): number => field.bandDensityOverField * enemyDensityPerM2;

/**
 * The other enemies a region holds when `T` stands at distance `D`: the field's
 * density integrated over the part of the region outside the empty disc.
 */
function othersMass(cells: Cells, frame: FieldRegion['frame'], distanceM: number, field: CastFieldEvidence, enemyDensityPerM2: number): number {
  let sum = 0;
  for (let i = 0; i < cells.xs.length; i += 1) {
    sum += othersDensityShape(cells.xs[i]!, cells.ys[i]!, frame, distanceM, field);
  }
  return sum * cells.cellAreaM2 * field.bandDensityOverField * enemyDensityPerM2;
}

/** One draw of the distance law: how likely it is, whether `T` is struck, and how many others the region holds. */
export interface FieldNode {
  readonly weight: number;
  readonly targetInside: boolean;
  readonly others: number;
}

const MAX_PROFILES = 2048;
const profiles = new Map<string, readonly FieldNode[]>();

/** `reachM` is how far the cast reaches ahead of the caster before it fires; `entry` fires it as an enemy first steps into the region. */
export function castFieldProfile(region: FieldRegion, reachM: number, field: CastFieldEvidence, enemyDensityPerM2: number, entry = false): readonly FieldNode[] {
  const key = `${region.key}|${reachM}|${enemyDensityPerM2}|${entry}|${JSON.stringify(field)}`;
  const known = profiles.get(key);
  if (known !== undefined) return known;
  const cells = sampleCells(region);
  const nodes = castDistances(reachM, field, entry).map(({ distanceM, weight }): FieldNode => ({
    weight,
    targetInside: region.frame === 'TARGET' ? region.contains(0, 0) : region.contains(0, distanceM),
    others: othersMass(cells, region.frame, distanceM, field, enemyDensityPerM2),
  }));
  if (profiles.size >= MAX_PROFILES) profiles.clear();
  profiles.set(key, nodes);
  return nodes;
}

/** One draw of the distance law, for a region read against a disc around the caster: how likely it is, and who stands in the region and within the disc. */
export interface ReachNode {
  readonly weight: number;
  readonly targetInside: boolean;
  /** `T` is a body in the region at distance `D <= withinM` from the caster. */
  readonly targetWithin: boolean;
  /** The other enemies the whole region holds. */
  readonly othersAll: number;
  /** The other enemies the region holds no farther than `withinM` from the caster. */
  readonly othersWithin: number;
  /** How many other enemies of the region stand within `withinM` of one of them (the average over them): where the region's enemies crowd, more than its area alone says. */
  readonly othersNearAnOther: number;
}

/** The cells the neighbours of an enemy are counted on: finer than the jump of a chain, coarse enough to pair. */
const NEIGHBOUR_CELLS = 1200;

/** The average over the density `shape` of the shape mass within `nearM` of a cell, over the shape mass: `sum_i sum_j s_i s_j [d_ij <= nearM] / sum_i s_i`, per cell. */
function neighbourMass(cells: Cells, shapeOf: (i: number) => number, nearM: number): number {
  // The cells are in ascending x, so the neighbours of a cell are a window of them.
  const xs: number[] = [];
  const ys: number[] = [];
  const shapes: number[] = [];
  for (let i = 0; i < cells.xs.length; i += 1) {
    const shape = shapeOf(i);
    if (shape > 0) { xs.push(cells.xs[i]!); ys.push(cells.ys[i]!); shapes.push(shape); }
  }
  const nearSquared = nearM * nearM;
  let pairs = 0;
  let total = 0;
  let first = 0;
  for (let i = 0; i < xs.length; i += 1) {
    total += shapes[i]!;
    while (xs[i]! - xs[first]! > nearM) first += 1;
    for (let j = first; j < xs.length && xs[j]! - xs[i]! <= nearM; j += 1) {
      if (i !== j && (xs[i]! - xs[j]!) ** 2 + (ys[i]! - ys[j]!) ** 2 <= nearSquared) pairs += shapes[i]! * shapes[j]!;
    }
  }
  return total > 0 ? pairs / total : 0;
}

const reachProfiles = new Map<string, readonly ReachNode[]>();

/**
 * `castFieldProfile` of a region, also read against the disc of radius `withinM` around the caster: the enemies of the
 * region that stand within the caster's reach. In the `TARGET` frame the caster stands at `(0, -D)`.
 */
export function castFieldReachProfile(region: FieldRegion, reachM: number, withinM: number, field: CastFieldEvidence, enemyDensityPerM2: number): readonly ReachNode[] {
  const key = `${region.key}|${reachM}|${withinM}|${enemyDensityPerM2}|${JSON.stringify(field)}`;
  const known = reachProfiles.get(key);
  if (known !== undefined) return known;
  const cells = sampleCells(region);
  const coarse = sampleCells(region, NEIGHBOUR_CELLS);
  const scale = cells.cellAreaM2 * field.bandDensityOverField * enemyDensityPerM2;
  const nodes = castDistances(reachM, field).map(({ distanceM, weight }): ReachNode => {
    const casterY = region.frame === 'TARGET' ? -distanceM : 0;
    let all = 0;
    let within = 0;
    for (let i = 0; i < cells.xs.length; i += 1) {
      const shape = othersDensityShape(cells.xs[i]!, cells.ys[i]!, region.frame, distanceM, field);
      all += shape;
      if (Math.hypot(cells.xs[i]!, cells.ys[i]! - casterY) <= withinM) within += shape;
    }
    return {
      weight,
      targetInside: region.frame === 'TARGET' ? region.contains(0, 0) : region.contains(0, distanceM),
      targetWithin: distanceM <= withinM,
      othersAll: all * scale,
      othersWithin: within * scale,
      othersNearAnOther: neighbourMass(coarse, (i) => othersDensityShape(coarse.xs[i]!, coarse.ys[i]!, region.frame, distanceM, field), withinM) * coarse.cellAreaM2 * field.bandDensityOverField * enemyDensityPerM2,
    };
  });
  if (reachProfiles.size >= MAX_PROFILES) reachProfiles.clear();
  reachProfiles.set(key, nodes);
  return nodes;
}
